import type { ProductItemGroup } from '@/types/censhare/productItemGroup';
import log from '~/utils/log';
import slugify from '~/utils/slugify';

/**
 * Pure helpers around the PDP slug/ID map. Bundled for the browser as well -
 * components import `getSlugForContext` from here - so nothing in this file may
 * fetch, cache or otherwise touch server-only APIs.
 *
 * Fetching the map from Censhare and caching it lives in
 * server/utils/censhare/slugMap.ts.
 */

export type InternationalSlugToIdMap = {
    [country: string]: {
        [language: string]: SlugToIdMap;
    };
};

export type ProductSlugs = {
    [country: string]: {
        [language: string]: string;
    };
};

type SlugToIdMap = {
    [slug: string]: string;
};

export const FALLBACK_SLUG_LANGUAGE = '_fallback';

function getCountries(group: ProductItemGroup) {
    return (group.features?.country_of_distribution || [])
        .map(({ iso2 }) => iso2?.toLowerCase())
        .filter((country): country is string => Boolean(country));
}

export function buildSlugMap(productItemGroups: ProductItemGroup[]): InternationalSlugToIdMap {
    const map: InternationalSlugToIdMap = {};

    for (const group of productItemGroups) {
        const countries = getCountries(group);
        if (!countries.length) continue;

        for (const country of countries) {
            if (!map[country]) {
                map[country] = {};
            }

            const localizedNames = Object.entries(group.nameLocalized || {});
            for (const [language, name] of localizedNames) {
                addSlugMapping(map, country, language, name, group.id);
            }

            if (group.name) {
                // Censhare may not provide a title for the requested locale. Keep
                // its technical product name as a country-scoped fallback instead.
                addSlugMapping(map, country, FALLBACK_SLUG_LANGUAGE, group.name, group.id);
            }
        }
    }

    return map;
}

export function getProductIdForContext(map: InternationalSlugToIdMap, country: string, language: string, slug: string) {
    const countryMap = map[country.toLowerCase()];
    return countryMap?.[language]?.[slug] ?? countryMap?.[FALLBACK_SLUG_LANGUAGE]?.[slug];
}

function addSlugMapping(map: InternationalSlugToIdMap, country: string, language: string, name: string, id: number) {
    const countryMap = (map[country] ??= {});
    const languageMap = (countryMap[language] ??= {});

    const slug = slugify(name);
    const existingId = languageMap[slug];

    if (existingId && existingId !== id.toString()) {
        log.warn(
            `[censhare] duplicate PDP slug mapping for country=${country} lang=${language} slug=${slug}: ${existingId} -> ${id}`
        );
    }

    languageMap[slug] = id.toString();
}

export function getProductSlugsForId(map: InternationalSlugToIdMap, id: number): ProductSlugs {
    const slugs: ProductSlugs = {};
    const idString = id.toString();

    for (const [country, languageMap] of Object.entries(map)) {
        for (const [language, slugMap] of Object.entries(languageMap)) {
            if (language === FALLBACK_SLUG_LANGUAGE) continue;

            for (const [slug, productId] of Object.entries(slugMap)) {
                if (productId === idString) {
                    slugs[country] ??= {};
                    slugs[country][language] = slug;
                }
            }
        }
    }

    return slugs;
}

export function getSlugForContext(slugs: ProductSlugs | undefined, country: string, lang: string) {
    return slugs?.[country?.toLowerCase()]?.[lang];
}
